import api from './api';
import { getPrefetchedStream, setPrefetchedStream } from '../utils/videoPrefetchCache';

/** Normalize backend stream/presign response shapes */
export const extractStreamUrl = (res) =>
  res?.streamUrl || res?.downloadUrl || res?.url || res?.signedUrl || null;

const TRANSCODE_POLL_MS = 500;
const TRANSCODE_POLL_MAX_ATTEMPTS = 240;
const RATE_LIMIT_BACKOFF_MS = 15000;
const prefetchInFlight = new Set();

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const isRateLimitError = (err) =>
  /too many requests|rate limit|429/i.test(err?.message || '');

const VIDEO_LIST_TIMEOUT_MS = 120000;
const VIDEO_STREAM_TIMEOUT_MS = 90000;

const extractVideoList = (res) => {
  if (Array.isArray(res)) return { videos: res, count: res.length };
  if (!res || typeof res !== 'object') return { videos: [] };
  const videos = Array.isArray(res.videos)
    ? res.videos
    : Array.isArray(res.data?.videos)
      ? res.data.videos
      : Array.isArray(res.data)
        ? res.data
        : [];
  return { ...res, videos, count: res.count ?? videos.length };
};

const buildVideoListParams = ({ bridgeId, start, end, sensorIds }) => {
  const params = { bridgeId };
  if (start) params.start = start;
  if (end) params.end = end;
  if (sensorIds) params.sensorIds = sensorIds;
  return params;
};

const isTranscodeInProgress = (res, httpStatus) =>
  httpStatus === 202
  || res?.transcodeStatus === 'pending'
  || res?.transcodeStatus === 'processing';

const videoService = {
  listEventVideos: ({ bridgeId, start, end } = {}) =>
    api.get(
      '/videos/event',
      buildVideoListParams({ bridgeId, start, end }),
      { timeout: VIDEO_LIST_TIMEOUT_MS }
    ).then(extractVideoList),

  listHealthVideos: ({ bridgeId, start, end, sensorIds } = {}) =>
    api.get(
      '/videos/health',
      buildVideoListParams({ bridgeId, start, end, sensorIds }),
      { timeout: VIDEO_LIST_TIMEOUT_MS }
    ).then(extractVideoList),

  getStreamUrl: async (videoId) => {
    const { status, data } = await api.getWithStatus(
      `/videos/${videoId}/stream`,
      undefined,
      { timeout: VIDEO_STREAM_TIMEOUT_MS }
    );
    const body = data && typeof data === 'object' ? data : {};
    return {
      ...body,
      httpStatus: status,
      streamUrl: extractStreamUrl(body),
    };
  },

  /** Warm stream URL on row hover; also starts transcode so click waits less. */
  prefetchStream: async (videoId, video) => {
    if (!videoId || getPrefetchedStream(videoId) || prefetchInFlight.has(videoId)) return null;
    prefetchInFlight.add(videoId);
    try {
      const res = await videoService.getStreamUrl(videoId);
      if (
        res.streamUrl
        && res.httpStatus !== 202
        && res.transcodeStatus !== 'processing'
        && res.transcodeStatus !== 'pending'
        && (video?.type !== 'event' || res.audioGated === true)
      ) {
        setPrefetchedStream(videoId, res, video?.impactAudioGateVersion);
        return res;
      }
    } catch {
      // Ignore prefetch failures
    } finally {
      prefetchInFlight.delete(videoId);
    }
    return null;
  },

  getTranscodeStatus: (videoId) =>
    api.get(`/videos/${videoId}/transcode-status`),

  /**
   * Poll until transcodeStatus === 'ready', then return final stream response.
   * @param {string} videoId
   * @param {() => boolean} shouldContinue - return false to abort (modal closed)
   */
  waitUntilStreamReady: async (videoId, shouldContinue = () => true) => {
    let rateLimitStrikes = 0;

    for (let attempt = 0; attempt < TRANSCODE_POLL_MAX_ATTEMPTS; attempt += 1) {
      if (!shouldContinue()) {
        throw new Error('Video preparation cancelled');
      }

      try {
        const statusRes = await videoService.getTranscodeStatus(videoId);
        rateLimitStrikes = 0;

        if (statusRes.transcodeStatus === 'failed') {
          throw new Error(statusRes.transcodeError || statusRes.message || 'Video transcoding failed');
        }

        const streamRes = await videoService.getStreamUrl(videoId);
        if (streamRes.streamUrl && streamRes.httpStatus !== 202) {
          if (streamRes.type === 'event' && streamRes.audioGated !== true) {
            await sleep(TRANSCODE_POLL_MS);
            continue;
          }
          return streamRes;
        }
      } catch (err) {
        if (isRateLimitError(err)) {
          rateLimitStrikes += 1;
          if (rateLimitStrikes >= 3) {
            throw new Error('Too many requests. Please wait a moment and try again.');
          }
          await sleep(RATE_LIMIT_BACKOFF_MS);
          continue;
        }
        throw err;
      }

      await sleep(TRANSCODE_POLL_MS);
    }

    throw new Error('Video preparation timed out — please try again later');
  },

  /**
   * Stream URL with automatic transcode polling when backend returns 202.
   */
  getPlaybackStream: async (videoId, shouldContinue = () => true) => {
    let streamRes = await videoService.getStreamUrl(videoId);

    if (streamRes.streamUrl && !isTranscodeInProgress(streamRes, streamRes.httpStatus)) {
      return streamRes;
    }

    if (isTranscodeInProgress(streamRes, streamRes.httpStatus)) {
      return videoService.waitUntilStreamReady(videoId, shouldContinue);
    }

    if (streamRes.streamUrl) {
      return streamRes;
    }

    throw new Error(streamRes.message || 'Could not get playback URL');
  },

  getDownloadUrl: async (videoId) => {
    const res = await api.get(`/videos/${videoId}/download`);
    return { ...res, downloadUrl: extractStreamUrl(res) };
  },

  presignByFilename: async ({ bridgeId, filename }) => {
    const res = await api.get('/videos/presign', { bridgeId, filename });
    return { ...res, streamUrl: extractStreamUrl(res) };
  },

  registerVideos: ({ bridgeId, filenames, verify = true }) =>
    api.post('/videos/register', { bridgeId, filenames, verify }),

  deleteVideo: (videoId) =>
    api.delete(`/videos/${videoId}`),

  syncFromS3: (bridgeId) =>
    api.post(`/videos/sync?bridgeId=${encodeURIComponent(bridgeId)}`),
};

export default videoService;
